import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InformationCircleIcon } from '@inter-ikea-kompis/icons';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { metricLengthToFormattedImperial } from '@inter-ikea-guida/framework';
import { SkapaColors } from '@inter-ikea-guida/skapa';
import { useKompis } from '../../contexts';
import { useIsMetricMeasures } from '../../hooks/useIsMetricMeasures';
import useIsRtl from '../../hooks/useIsRtl';
import { KompisIcon, KompisPriceModule } from '../../kompis';
import { getFormState } from '../../store/app/selectors';
import { getTranslation } from '../../store/i18n/selectors';
import { WALL, SINGLE_WALL, PAX } from '../../utils';
import { buildShoppingProducts } from '../../utils/results';
import { getCornerWidths } from './utils';
const parseDimensions = (measurements) => measurements.map(m => parseInt(m));
const convertDimensionsToImperial = (measurements, kompisTranslations) => parseDimensions(measurements).map(m => metricLengthToFormattedImperial(m, kompisTranslations));
const formatDimensions = (measurements, isCorner) => isCorner
    ? `${measurements[0]}/${measurements[1]}x${measurements[2]}`
    : `${measurements[0]}x${measurements[1]}x${measurements[2]}`;
const StyledPriceModuleWrapper = styled.div `
  position: relative;
  width: 100%;
`;
const StyledInformationIconWrapper = styled.div `
  position: absolute;
  top: 0;
  right: ${({ $rtl }) => ($rtl ? 'auto' : 0)};
  left: ${({ $rtl }) => ($rtl ? 0 : 'auto')};
  color: ${SkapaColors.colourStaticBlack};
`;
/**
 * Get the selected design dimensions. For PAX, we can use the selected values from the form.
 * @param form The form state
 * @param isMetric Should metric values be used?
 * @returns A string containing the design dimensions.
 */
const getDesignDimensions = (form, isMetric, kompisTranslations) => {
    const isCorner = form[WALL] !== SINGLE_WALL;
    if (isCorner) {
        const cornerWidths = getCornerWidths(form);
        if (cornerWidths[0] && cornerWidths[1] && form.size_height) {
            return isMetric
                ? formatDimensions([...cornerWidths, form.size_height], isCorner)
                : formatDimensions(convertDimensionsToImperial([...cornerWidths, form.size_height], kompisTranslations), isCorner);
        }
        return undefined;
    }
    if (form.size_width && form.size_depth && form.size_height) {
        return isMetric
            ? formatDimensions([form.size_width, form.size_depth, form.size_height], isCorner)
            : formatDimensions(convertDimensionsToImperial([form.size_width, form.size_depth, form.size_height], kompisTranslations), isCorner);
    }
    return undefined;
};
export const PriceModule = ({ itemList, products, shoppingProducts, layout, id, forcePointer, showInformationIcon }) => {
    const isRtl = useIsRtl();
    const useMetricsMeasures = useIsMetricMeasures();
    const form = useSelector(getFormState);
    const translate = useSelector(getTranslation);
    const { translations } = useKompis();
    const [hasPointerSet, setHasPointerSet] = useState(false);
    // HACK: Set correct cursor type for KOMPIS product content
    useEffect(() => {
        if (hasPointerSet || !forcePointer) {
            return;
        }
        const observer = new MutationObserver(() => {
            const contentElement = document
                .querySelector('#guida-wrapper')
                ?.querySelector(`#${id}`)
                ?.querySelector('kompis-price-module')
                ?.shadowRoot.querySelector('.productContent');
            if (contentElement) {
                // Force cursor type pointer
                contentElement.style.cursor = 'pointer';
                setHasPointerSet(true);
            }
        });
        observer.observe(document, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
        };
    }, [forcePointer, hasPointerSet, id]);
    if (!shoppingProducts && (!itemList.length || !products.length)) {
        return _jsx(_Fragment, {});
    }
    const moduleShoppingProducts = shoppingProducts ?? buildShoppingProducts(itemList, products);
    const productName = PAX.toUpperCase();
    const dimensions = getDesignDimensions(form, useMetricsMeasures, translations.kompis);
    const unit = useMetricsMeasures
        ? translate('Common.MeasureValueCm')
        : translate('Common.MeasureValueIn');
    const productDescription = dimensions ? `${dimensions} ${unit}` : undefined;
    return (_jsxs(StyledPriceModuleWrapper, { id: id, children: [showInformationIcon && (_jsx(StyledInformationIconWrapper, { "$rtl": isRtl, children: _jsx(KompisIcon, { icon: InformationCircleIcon }) })), _jsx(KompisPriceModule, { shoppingProducts: moduleShoppingProducts, productName: productName, layout: layout, children: productDescription })] }));
};
//# sourceMappingURL=PriceModule.js.map